import { createSlice } from "@reduxjs/toolkit";
import { notification } from "antd";
import i18next from "i18next";

const propertySlice = createSlice({
  name: "user",
  initialState: {
    userInfo: null,
    loading: false,
    error: null,
    changePasswordLoading: false,
    userDetails: null,
    userLoading: false,
    userError: null,
    userImageUploadLoading: false,
    userImageUploadError: null,
    userImageUploadRes: null,
    userImageDeleteLoading: false,
    favoriteUserProfiles: [],
    favoriteProfilesLoading: false,
    favoriteProfilesCurrentPage: 0,
    favoriteProfilesTotalPages: 0,
    loadingFav: false,
    totalAvailableListings: null,
    totalAvailableChatbotQueries: null
  },
  reducers: {
    // User actions
    userReset: (state) => {
      state.userInfo = null;
      state.loading = false;
      state.error = null;
    },
    userRequest: (state) => {
      state.userInfo = null;
      state.loading = true;
      state.error = null;
    },
    userSuccess: (state, action) => {
      const data = action.payload;
      state.userInfo = data;
      state.totalAvailableListings = action.payload?.totalAvailableListings;
      state.loading = false;
      state.error = null;
    },
    userFailure: (state, action) => {
      state.error = action.payload?.response?.data?.details;
      state.loading = false;
    },
    changePasswordRequest: (state) => {
      state.error = null;
      state.changePasswordLoading = true;
    },
    changePasswordSuccess: (state) => {
      state.changePasswordLoading = false;
      state.userInfo = { ...state.userInfo, isPasswordSet: true };
      notification.success({
        message: i18next.t("changePassword.successTitle"),
        description: i18next.t("changePassword.successMessage"),
        placement: "topRight"
      });
    },

    changePasswordFailure: (state, action) => {
      state.error = action.payload?.response?.data?.message;
      state.changePasswordLoading = false;
    },
    getUserDetailsRequest: (state) => {
      state.userDetails = null;
      state.userLoading = true;
      state.userError = null;
      state.totalAvailableListings = null;
      state.totalAvailableChatbotQueries = null;
    },
    getUserDetailsSuccess: (state, action) => {
      state.userDetails = action.payload;
      state.totalAvailableListings = action.payload?.totalAvailableListings;
      state.totalAvailableChatbotQueries =
        action.payload.totalAvailableChatbotQueries;
      state.userLoading = false;
      state.userError = null;
    },
    getUserDetailsFailure: (state, action) => {
      state.userDetails = null;
      state.totalAvailableListings = null;
      state.userLoading = false;
      state.userError = action.payload?.response?.data?.details;
    },
    resetTotalAvailableListings: (state) => {
      state.totalAvailableListings = null;
    },
    increaseTotalAvailableListings: (state) => {
      state.totalAvailableListings = state.totalAvailableListings + 1;
    },
    decreaseTotalAvailableListings: (state) => {
      if (state.totalAvailableListings > 0) {
        state.totalAvailableListings = state.totalAvailableListings - 1;
      }
      return;
    },
    resetTotalAvailableChatbotQueries: (state) => {
      state.totalAvailableChatbotQueries = null;
    },
    increaseTotalAvailableChatbotQueries: (state) => {
      state.totalAvailableChatbotQueries =
        state.totalAvailableChatbotQueries + 1;
    },
    decreaseTotalAvailableChatbotQueries: (state) => {
      if (state.totalAvailableChatbotQueries > 0) {
        state.totalAvailableChatbotQueries =
          state.totalAvailableChatbotQueries - 1;
      }
    },
    userImageUploadRequest: (state) => {
      state.userImageUploadLoading = true;
      state.userImageUploadError = null;
      state.userImageUploadRes = null;
    },
    userImageUploadSuccess: (state, action) => {
      state.userImageUploadLoading = false;
      state.userImageUploadError = null;
      state.userInfo = {
        ...state.userInfo,
        profilePictureUrl: action?.payload?.profilePictureUrl
      };
      state.userImageUploadRes = "Success responce";
    },
    userImageUploadFailure: (state) => {
      state.userImageUploadLoading = false;
      state.userImageUploadError = "Error Responnce";
      state.userImageUploadRes = null;
    },
    userUpdateRequest: (state) => {
      state.userUpdateLoading = true;
      state.userUpdateError = null;
    },
    userUpdateSuccess: (state, action) => {
      state.userUpdateLoading = false;
      state.userUpdateError = null;
      state.userInfo = action.payload;
      notification.success({
        message: i18next.t("profileSettings.updateSuccessTitle"),
        description: i18next.t("profileSettings.updateSuccessMessage"),
        placement: "topRight"
      });
    },
    userUpdateFailure: (state, action) => {
      state.userUpdateLoading = false;
      state.userUpdateError = action.payload?.response?.data?.details;
      notification.error({
        message: i18next.t("profileSettings.updateFailedTitle"),
        description: action?.payload?.response?.data?.message,
        placement: "topRight"
      });
    },

    userImageDeleteRequest: (state) => {
      state.userImageDeleteLoading = true;
    },

    userImageDeleteSuccess: (state) => {
      state.userImageDeleteLoading = false;
      state.userInfo = {
        ...state.userInfo,
        profilePictureUrl: null
      };
      notification.success({
        message: i18next.t("profileSettings.imageDeletedTitle"),
        description: i18next.t("profileSettings.imageDeletedMessage"),
        placement: "topRight"
      });
    },

    userImageDeleteFailure: (state, action) => {
      state.userImageDeleteLoading = false;
      notification.error({
        message: i18next.t("myProperties.imageDeleteFailed"),
        description: action?.payload?.response?.data?.message,
        placement: "topRight"
      });
    },

    favoriteUserProfilesRequest: (state) => {
      state.favoriteProfilesLoading = true;
    },

    favoriteUserProfilesSuccess: (state, action) => {
      const {
        favouriteUserProfileResponses = [],
        totalPages,
        currentPage
      } = action.payload;

      if (currentPage === 0) {
        state.favoriteUserProfiles = favouriteUserProfileResponses;
      } else {
        const newProfiles = favouriteUserProfileResponses?.filter(
          (profile) =>
            !state.favoriteUserProfiles?.some(
              (existingProfile) => existingProfile?.uuid === profile?.uuid
            )
        );

        state.favoriteUserProfiles = [
          ...state.favoriteUserProfiles,
          ...newProfiles
        ];
      }

      state.favoriteProfilesTotalPages = totalPages;
      state.favoriteProfilesCurrentPage = currentPage;
      state.favoriteProfilesLoading = false;
    },

    favoriteUserProfilesFailure: (state, action) => {
      state.favoriteProfilesLoading = false;
      notification.error({
        message: i18next.t("favorite.favoriteProfileFailed"),
        description: action?.payload?.response?.data?.message,
        placement: "topRight"
      });
    },

    resetFavoriteUserProfilesList: (state) => {
      state.favoriteUserProfiles = [];
    },
    markProfileAsFavoriteRequest: (state) => {
      state.loadingFav = true;
      state.userDetails = {
        ...state.userDetails,
        isUserProfileFavourite: true
      };
    },
    markProfileAsFavoriteSuccess: (state) => {
      state.loadingFav = false;
    },
    markProfileAsFavoriteFailure: (state, action) => {
      state.loadingFav = false;
      state.error = action.payload;
      notification.error({
        message: i18next.t("favorite.markProfileFailedTitle"),
        description: i18next.t("favorite.markProfileFailedMessage"),
        placement: "topRight"
      });
    },
    unMarkProfileAsFavoriteRequest: (state, action) => {
      const { userProfileId, isProfileCard } = action.payload;
      if (isProfileCard) {
        state.favoriteUserProfiles = state.favoriteUserProfiles.filter(
          (item) => item.favouriteUserId !== userProfileId
        );
      }

      state.loadingFav = true;
      state.userDetails = {
        ...state.userDetails,
        isUserProfileFavourite: false
      };
    },
    unMarkProfileAsFavoriteSuccess: (state) => {
      state.loadingFav = false;
    },
    unMarkProfileAsFavoriteFailure: (state, action) => {
      state.loadingFav = false;
      state.error = action.payload;
      notification.error({
        message: i18next.t("favorite.removeProfileFailedTitle"),
        description: i18next.t("favorite.removeProfileFailedMessage"),
        placement: "topRight"
      });
    },
    resetErrorMessage: (state) => {
      state.error = null;
      state.userError = null;
      state.userImageUploadError = null;
    }
  }
});

export const {
  userRequest,
  userSuccess,
  userFailure,
  changePasswordRequest,
  changePasswordSuccess,
  changePasswordFailure,
  userReset,
  getUserDetailsRequest,
  getUserDetailsSuccess,
  getUserDetailsFailure,
  userImageUploadRequest,
  userImageUploadSuccess,
  userImageUploadFailure,
  userUpdateRequest,
  userUpdateSuccess,
  userUpdateFailure,
  userImageDeleteRequest,
  userImageDeleteSuccess,
  userImageDeleteFailure,
  favoriteUserProfilesRequest,
  favoriteUserProfilesSuccess,
  favoriteUserProfilesFailure,
  resetFavoriteUserProfilesList,
  markProfileAsFavoriteRequest,
  markProfileAsFavoriteSuccess,
  markProfileAsFavoriteFailure,
  unMarkProfileAsFavoriteRequest,
  unMarkProfileAsFavoriteSuccess,
  unMarkProfileAsFavoriteFailure,
  totalAvailableListingsRequest,
  totalAvailableListingsSuccess,
  resetTotalAvailableListings,
  increaseTotalAvailableListings,
  decreaseTotalAvailableListings,
  resetTotalAvailableChatbotQueries,
  increaseTotalAvailableChatbotQueries,
  decreaseTotalAvailableChatbotQueries,
  resetErrorMessage
} = propertySlice.actions;

export default propertySlice.reducer;
